/* eslint-disable indent */
import _, { isEmpty, isFunction } from 'lodash';
import debounce from 'lodash/debounce';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { v4 as uuid, validate } from 'uuid';
import QCXDeclaracaoImportacaoWizardFinalForm from '../../../../components/declaracao-importacao/QCXDeclaracaoImportacaoWizardFinalForm';
import { changeControlTo as changeControlCatalogoProduto } from '../../../../features/catalogo-produtos/catalogoProdutosSlice';
import { changeControlTo as changeControlCfopTo } from '../../../../features/cfop-operacoes-fiscais/cfopOperacoesFiscaisSlice';
import { clienteActions } from '../../../../features/cliente/clienteSlice';
import { AUTO_LOAD_CONTROL, IDLE_CONTROL } from '../../../../features/config-control';
import {
  selectBackgroundMode,
  selectDeclaracaoImportacao,
  selectMode,
  selectStatus,
  selectSubBackgroundMode,
} from '../../../../features/declaracao-importacao/declaracaoImportacaoSelectors';
import {
  addToList,
  changeToBackgroundCreateMode,
  changeToBackgroundDeleteMode,
  changeToConsultMode,
  changeToCreateMode,
  changeToUpdateMode,
  failure,
  loading,
  preparingAction,
  resetAdicoes,
  resetBackgroundMode,
  resetModel,
  resetRelatedAdicaoModel,
  resetStatus,
  setModel,
  success,
  updateOnList,
} from '../../../../features/declaracao-importacao/declaracaoImportacaoSlice';
import {
  fetchAdicoesByDiDuimpIdAsync,
  fetchByIdFromFaturaAsync,
  fetchCapaDiDuimpByIdFromFaturaAsync,
  fetchPaginatedMercadoriasAsync,
  syncAdicoesAsync,
} from '../../../../features/declaracao-importacao/declaracaoImportacaoThunks';
import { faturaAPI } from '../../../../features/fatura/faturaAPI';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { followUpAPI } from '../../../../features/follow-up/followUpAPI';
import { changeControlTo as changeControlMotivoImportacaoSemCoberturaCambialTo } from '../../../../features/motivo-importacao-sem-cobertura-cambial/motivoImportacaoSemCoberturaCambialSlice';
import { changeControlTo as changeControlNaladiNccaTo } from '../../../../features/naladi-ncca/naladiNccaSlice';
import { changeControlTo as changeControlNaladiShTo } from '../../../../features/naladi-sh/naladiShSlice';
import {
  changeControlTo as changeControlNcmTo,
  resetList as resetNcmList,
} from '../../../../features/ncm-subitem/ncmSubitemSlice';
import { changeControlTo as changeControlPaisTo } from '../../../../features/pais/paisSlice';
import { changeControlTo as changeControlRecintoAduaneiroTo } from '../../../../features/recinto-aduaneiro/recintoAduaneiroSlice';
import { changeControlTo as changeControlRegimeTributacaoTo } from '../../../../features/regime-tributacao/regimeTributacaoSlice';
import { changeControlTo as changeControlSetorLotacaoTo } from '../../../../features/setor-lotacao/setorLotacaoSlice';
import { changeControlTo as changeControlUnidadeMedidaTo } from '../../../../features/unidade-medida/unidadeDeMedidaSlice';
import QCXConfirmDialog from '../../../../shared-components/dialog/QCXConfirmDialog';
import { MODALIDADE_DESPACHO_NORMAL } from '../../../../shared-components/select-modalidade-despacho/modalidadeDespachoUtils';
import {
  TIPO_ACRESCIMO,
  TIPO_DEDUCAO,
} from '../../../../shared-components/select-tipo-acrescimo-deducao/tipoAcrescimoDeducaoUtils';
import { UTILIZACAO_TRANSPORTE_TOTAL } from '../../../../shared-components/select-utilizacao-transporte/utilizacaoTransporteUtils';
import QCXRegistrationFormPageTemplate from '../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate';
import { MediaType } from '../../../../utils/api/api-utils';
import { TIPO_DI_DUIMP } from '../../../../utils/general/fatura/faturaUtils';
import {
  forceUnnormalizeNumeral,
  isValid,
  normalizeData,
  normalizeNumeral,
  normalizeYear,
  unnormalizeData,
  unnormalizeNumeral,
} from '../../../../utils/general/general-utils';

import {
  convertLocalDateTimeStringToUTC,
  convertUTCStringToLocalDateTime,
} from '../../../../utils/general/dateTime-utils.ts';

import { isLiberadaStatus } from '../../../../utils/general/operations/operationUtils';
import {
  formatBrazilianNumericDecimal,
  formatBrazilianNumericDecimalWithoutSeparator,
} from '../../../../utils/hooks/form/field/formatters';
import useOperationConfirm from '../../../../utils/hooks/operation/confirm/useOperationConfirm';
import {
  isBackgroundCreateMode,
  isBackgroundDeleteMode,
  isConsultMode,
  isCreateMode,
  isFailureStatus,
  isIdleStatus,
  isLoadingStatus,
  isLockedMode,
  isNoneMode,
  isPreparingActionStatus,
  isSubBackgroundConsultMode,
  isSubBackgroundCreateMode,
  isSubBackgroundDeleteMode,
  isSubBackgroundUpdateMode,
  isUpdateMode,
} from '../../../../utils/store/store-utils';
import useConfigurePopUp from '../../../../ts/common/hooks/popUps/useConfigurePopUp';
import { limparAcrescimosAsync } from '../../../../features/fatura/faturaThunks';
import InformacoesComplementaresDiffDialog from './InformacoesComplementaresDiffDialog';
import { declaracaoImportacaoAPI } from '../../../../features/declaracao-importacao/declaracaoImportacaoAPI';
import { parseToBoolean } from '../../../../utils/general/parse-utils';
import { normalizeAtributosDinamicosNcm } from '../../../../components/fatura/mercadoriaUtils';
import DeclaracaoImportacaoContext from '../../../../contexts/import/declaracao-importacao/DeclaracaoImportacaoContext';

export default function DeclaracaoImportacaoRegistrationPage({ authInfo = {} }) {
  const { fetchFaturaDiDuimpCapaByFaturaId: fetchById } = useContext(DeclaracaoImportacaoContext);

  const { t } = useTranslation();
  const params = useParams();

  const history = useHistory();

  const dispatch = useDispatch();

  const [isCreated, setIsCreated] = useState(false);

  const status = useSelector(selectStatus);
  const mode = useSelector(selectMode);
  const backgroundMode = useSelector(selectBackgroundMode);
  const subBackgroundMode = useSelector(selectSubBackgroundMode);
  const declaracaoImportacao = useSelector(selectDeclaracaoImportacao);

  const isPreparingAction = useMemo(() => isPreparingActionStatus(status), [status]);

  const isLoading = useMemo(() => isLoadingStatus(status), [status]);

  const isIdle = useMemo(() => isIdleStatus(status), [status]);

  const isFailure = useMemo(() => isFailureStatus(status), [status]);

  const isNone = useMemo(() => isNoneMode(mode), [mode]);

  const isCreate = useMemo(() => isCreateMode(mode), [mode]);

  const isConsult = useMemo(() => isConsultMode(mode), [mode]);

  const isUpdate = useMemo(() => isUpdateMode(mode), [mode]);

  const isLocked = useMemo(() => isLockedMode(mode), [mode]);

  const isBackgroundCreate = useMemo(() => isBackgroundCreateMode(backgroundMode), [backgroundMode]);

  const isBackgroundDelete = useMemo(() => isBackgroundDeleteMode(backgroundMode), [backgroundMode]);

  const isSubBackgroundCreate = useMemo(() => isSubBackgroundCreateMode(subBackgroundMode), [subBackgroundMode]);

  const isSubBackgroundConsult = useMemo(() => isSubBackgroundConsultMode(subBackgroundMode), [subBackgroundMode]);

  const isSubBackgroundUpdate = useMemo(() => isSubBackgroundUpdateMode(subBackgroundMode), [subBackgroundMode]);

  const isSubBackgroundDelete = useMemo(() => isSubBackgroundDeleteMode(subBackgroundMode), [subBackgroundMode]);

  const hasFetchedCapaRef = useRef(false);

  const handleChangeToPreparingAction = useCallback(() => {
    dispatch(preparingAction());
  }, []);

  const handleChangeToCreate = useCallback(() => {
    dispatch(changeToCreateMode());
  }, []);

  const handleChangeToBackgroundCreate = useCallback(() => {
    dispatch(changeToBackgroundCreateMode());
  }, []);

  const handleChangeToBackgroundDelete = useCallback(() => {
    dispatch(changeToBackgroundDeleteMode());
  }, []);

  const handleChangeToConsult = useCallback(() => {
    dispatch(changeToConsultMode());
  }, []);

  const handleChangeToUpdate = useCallback((additional = {}) => {
    dispatch(changeToUpdateMode());

    if (!isEmpty(additional) && isFunction(additional?.callback)) {
      additional.callback();
    }
  }, []);

  useEffect(() => {
    dispatch(resetStatus());

    dispatch(clienteActions.changeControlTo(IDLE_CONTROL));
    dispatch(changeControlCatalogoProduto(AUTO_LOAD_CONTROL));
    dispatch(changeControlPaisTo(AUTO_LOAD_CONTROL));
    dispatch(changeControlRecintoAduaneiroTo(IDLE_CONTROL));
    dispatch(changeControlSetorLotacaoTo(IDLE_CONTROL));
    dispatch(changeControlNcmTo(AUTO_LOAD_CONTROL));
    dispatch(changeControlNaladiShTo(AUTO_LOAD_CONTROL));
    dispatch(changeControlNaladiNccaTo(AUTO_LOAD_CONTROL));
    dispatch(changeControlUnidadeMedidaTo(AUTO_LOAD_CONTROL));
    dispatch(changeControlCfopTo(AUTO_LOAD_CONTROL));
    dispatch(changeControlRegimeTributacaoTo(IDLE_CONTROL));
    dispatch(changeControlMotivoImportacaoSemCoberturaCambialTo(IDLE_CONTROL));

    return () => {
      dispatch(clienteActions.resetControl());
      dispatch(resetRelatedAdicaoModel());
    };
  }, []);

  const handleResetBackgroundMode = useCallback(() => {
    dispatch(resetBackgroundMode());
  }, []);

  useEffect(() => {
    if (hasFetchedCapaRef.current) return;

    const handleFetchById = () => {
      if (!isEmpty(params?.id)) {
        fetchById(params?.id);

        handleChangeToConsult();
      }
    };

    handleFetchById();
    hasFetchedCapaRef.current = true;
  }, [params, handleChangeToConsult]);

  const handleRepassarMoedaMle = useCallback(({ form, values }) => {
    const adicoes = values?.atributosAdicionais?.adicoes || [];

    adicoes.forEach((adicao, index) => {
      if (values?.mle?.moeda) {
        form.change(`atributosAdicionais.adicoes[${index}].moeda`, values?.mle?.moeda);
      } else {
        form.change(`atributosAdicionais.adicoes[${index}].moeda`, null);
      }
    });
  }, []);

  const [openAskRepassarMoedasPopUp, askRepassarMoedasPopUpConfig] = useConfigurePopUp({
    primaryConfirmCallback: handleRepassarMoedaMle,
    initialOptions: { title: 'Moeda MLE Alterada', message: 'Deseja repassar a moeda para as adições?' },
  });

  const handleLimparAcrescimoDeducao = useCallback(() => {
    dispatch(limparAcrescimosAsync({ data: params?.id, finishCallback: handleBasicCancelUpdate }));
  }, [params]);

  const [openAskLimparAcrescimosDeducoes, askLimparAcrescimosDeducoesConfig] = useConfigurePopUp({
    primaryConfirmCallback: handleLimparAcrescimoDeducao,
    initialOptions: {
      title: 'Limpar Acréscimo/Dedução',
      message:
        'Atenção! Essa operação irá excluir os acréscimos e deduções das adições. Essa ação não poderá ser revertida. Deseja continuar?',
    },
  });

  const normalize = useCallback(
    (unnormalizedData) => {
      const {
        atributosAdicionais,
        importador,
        paisProcedencia,
        urfDespacho,
        urfEntrada,
        pesoLiquido,
        pesoBruto,
        data,
        rateiaPesoLiquidoItem,
        incotermCondicaoVenda,
        mle,
        despesa,
        frete,
        seguro,
        desconto,
        exportador,
        fabricante,
        paisOrigem,
        mercadorias,
        totaisAfrmm,
        despesasAcessorias,
        followUp,
        ...restUnnormalizedData
      } = unnormalizedData;

      const normalizedPagamentos = (atributosAdicionais?.pagamentos || [])?.map((pagamento) =>
        validate(pagamento?.id)
          ? {
              ...pagamento,
              id: null,
              valorReal: normalizeNumeral(pagamento?.valorReal),
              valorSiscomex: normalizeNumeral(pagamento?.valorSiscomex),
              codigoReceita: pagamento?.codigoReceita?.id ? pagamento?.codigoReceita : null,
              novoCodigoReceita: pagamento?.novoCodigoReceita?.id ? pagamento?.novoCodigoReceita : null,
            }
          : {
              ...pagamento,
              valorReal: normalizeNumeral(pagamento?.valorReal),
              valorSiscomex: normalizeNumeral(pagamento?.valorSiscomex),
              codigoReceita: pagamento?.codigoReceita?.id ? pagamento?.codigoReceita : null,
              novoCodigoReceita: pagamento?.novoCodigoReceita?.id ? pagamento?.novoCodigoReceita : null,
            }
      );

      const normalizedArmazens = (atributosAdicionais?.carga?.armazens || [])?.map((armazem) =>
        armazem?.id ? armazem?.value : armazem
      );

      const normalizedQuantidades = (atributosAdicionais?.carga?.quantidades || [])?.map((quantidade) =>
        validate(quantidade?.id)
          ? {
              ...quantidade,
              id: null,
              tipoEmbalagemNacional: quantidade?.tipoEmbalagemNacional?.id ? quantidade?.tipoEmbalagemNacional : null,
              quantidade: normalizeNumeral(quantidade?.quantidade),
            }
          : {
              ...quantidade,
              tipoEmbalagemNacional: quantidade?.tipoEmbalagemNacional?.id ? quantidade?.tipoEmbalagemNacional : null,
              quantidade: normalizeNumeral(quantidade?.quantidade),
            }
      );

      const normalizedDeclaracaoExportacoes = (atributosAdicionais?.carga?.declaracaoExportacoes || [])?.map((cde) =>
        validate(cde?.id)
          ? {
              ...cde,
              numero: cde.numero,
              inicio: cde.inicio,
              fim: cde.fim,
              id: null,
            }
          : {
              ...cde,
              numero: cde.numero,
              inicio: cde.inicio,
              fim: cde.fim,
            }
      );

      const normalizedVinculos = (atributosAdicionais?.vinculos || [])?.map((vinculo) =>
        validate(vinculo?.id)
          ? {
              ...vinculo,
              id: null,
            }
          : vinculo
      );

      const normalizedDocumentosInstrucoesDespacho = (atributosAdicionais?.documentosInstrucoesDespacho || [])?.map(
        (documentoInstrucaoDespacho) =>
          validate(documentoInstrucaoDespacho?.id)
            ? {
                ...documentoInstrucaoDespacho,
                id: null,
                documentoInstrucaoDespachoImportacao: documentoInstrucaoDespacho?.documentoInstrucaoDespachoImportacao
                  ?.id
                  ? documentoInstrucaoDespacho?.documentoInstrucaoDespachoImportacao
                  : null,
              }
            : documentoInstrucaoDespacho
      );

      const normalizedComponentesFrete = (atributosAdicionais?.componentesFrete || [])?.map((componenteFrete) => ({
        ...componenteFrete,
        id: validate(componenteFrete.id) ? null : componenteFrete.id,
        valor: componenteFrete.valor ? normalizeNumeral(componenteFrete.valor) : null,
        valorReal: componenteFrete.valorReal ? normalizeNumeral(componenteFrete.valorReal) : null,
      }));

      const mapAcrecimoDeducao = (acrecimoDeducao) => {
        const isUUid = validate(acrecimoDeducao?.id);
        let deducaoValoracaoAduaneira =
          acrecimoDeducao?.acrescimoValoracaoAduaneira?.id && acrecimoDeducao?.tipo === TIPO_DEDUCAO
            ? acrecimoDeducao?.acrescimoValoracaoAduaneira
            : null;

        if (acrecimoDeducao?.deducaoValoracaoAduaneira) {
          deducaoValoracaoAduaneira = acrecimoDeducao?.deducaoValoracaoAduaneira;
        }

        if (isUUid) {
          const obj = {
            ...acrecimoDeducao,
            id: null,
            valorMoeda: normalizeNumeral(acrecimoDeducao?.valorMoeda),
            valorReal: normalizeNumeral(acrecimoDeducao?.valorReal),
            moeda: acrecimoDeducao?.moeda?.id ? acrecimoDeducao?.moeda : null,
            acrescimoValoracaoAduaneira:
              acrecimoDeducao?.acrescimoValoracaoAduaneira?.id && acrecimoDeducao?.tipo === TIPO_ACRESCIMO
                ? acrecimoDeducao?.acrescimoValoracaoAduaneira
                : null,
            deducaoValoracaoAduaneira,
          };
          return obj;
        }

        const obj = {
          ...acrecimoDeducao,
          valorMoeda: normalizeNumeral(acrecimoDeducao?.valorMoeda),
          valorReal: normalizeNumeral(acrecimoDeducao?.valorReal),
          moeda: acrecimoDeducao?.moeda?.id ? acrecimoDeducao?.moeda : null,
          acrescimoValoracaoAduaneira:
            acrecimoDeducao?.acrescimoValoracaoAduaneira?.id && acrecimoDeducao?.tipo === TIPO_ACRESCIMO
              ? acrecimoDeducao?.acrescimoValoracaoAduaneira
              : null,
          deducaoValoracaoAduaneira,
        };

        return obj;
      };
      const normalizedAcrescimosDeducoes = (atributosAdicionais?.acrescimosDeducoes || [])?.map(mapAcrecimoDeducao);

      const normalizedMercadorias = mercadorias || [];
      const normalizedAdicoes =
        atributosAdicionais?.adicoes?.map((a) => ({ ...a, valorMleMoeda: normalizeNumeral(a.valorMleMoeda) })) || [];

      const normalizedAfmm = {
        ...totaisAfrmm,
        valorDolar: normalizeNumeral(totaisAfrmm?.valorDolar),
        valorReal: normalizeNumeral(totaisAfrmm?.valorReal),
        valorMoeda: normalizeNumeral(totaisAfrmm?.valorMoeda),
      };

      const normalizedDespesasAcessorias = {
        ...despesasAcessorias,
        valorReal: normalizeNumeral(despesasAcessorias?.valorReal),
        valorMoeda: normalizeNumeral(despesasAcessorias?.valorMoeda),
      };

      const normalizedData = {
        ..._.omit(restUnnormalizedData, 'ignorableFields'),
        data: normalizeData(data),
        followUp: {
          ...followUp,
        },
        importador: importador?.id ? importador : null,
        urfDespacho: urfDespacho?.id ? urfDespacho : null,
        pesoLiquido: normalizeNumeral(pesoLiquido),
        pesoBruto: normalizeNumeral(pesoBruto),
        rateiaPesoLiquidoItem,
        paisProcedencia: paisProcedencia?.id ? paisProcedencia : null,
        urfEntrada: urfEntrada?.id ? urfEntrada : null,
        incotermCondicaoVenda: incotermCondicaoVenda?.id ? incotermCondicaoVenda : null,
        atributosAdicionais: {
          ...atributosAdicionais,
          tipo: TIPO_DI_DUIMP,
          processo: atributosAdicionais?.processo,
          fundap: atributosAdicionais?.fundap,
          informacoesComplementares: atributosAdicionais?.informacoesComplementares,
          informacoesComplementaresCompleta:
            atributosAdicionais?.informacoesComplementares + atributosAdicionais?.informacoesComplementaresCalculada,
          informacoesComplementaresCalculada: atributosAdicionais?.informacoesComplementaresCalculada,
          tempoPermanencia: atributosAdicionais?.tempoPermanencia,
          vidaUtil: atributosAdicionais?.vidaUtil,
          caracterizacaoOperacao: atributosAdicionais?.caracterizacaoOperacao,
          modalidadeDespacho: atributosAdicionais?.modalidadeDespacho,
          vinculacaoCompradorVendedor: atributosAdicionais?.vinculacaoCompradorVendedor,
          tipoDeclaracao: atributosAdicionais?.tipoDeclaracao?.id ? atributosAdicionais?.tipoDeclaracao : null,
          acrescimosDeducoes: normalizedAcrescimosDeducoes,
          vinculos: normalizedVinculos,
          documentosInstrucoesDespacho: normalizedDocumentosInstrucoesDespacho,
          pagamentos: normalizedPagamentos,
          componentesFrete: normalizedComponentesFrete,
          dadosBancarios: {
            ...atributosAdicionais?.dadosBancarios,
            banco: atributosAdicionais?.dadosBancarios?.banco?.id ? atributosAdicionais?.dadosBancarios?.banco : null,
          },
          transporte: {
            ...atributosAdicionais?.transporte,
            dataEmbarque: convertLocalDateTimeStringToUTC(atributosAdicionais?.transporte?.dataEmbarque),
            dataPostagem: normalizeData(atributosAdicionais?.transporte?.dataPostagem),
            viaTransporte: atributosAdicionais?.transporte?.viaTransporte?.id
              ? atributosAdicionais?.transporte?.viaTransporte
              : null,
            transportadorInternacional:
              atributosAdicionais?.transporte?.transportadorInternacional?.transportador?.id ||
              atributosAdicionais?.transporte?.transportadorInternacional?.pais?.id
                ? {
                    ...atributosAdicionais?.transporte?.transportadorInternacional,
                    transportador: atributosAdicionais?.transporte?.transportadorInternacional?.transportador?.id
                      ? atributosAdicionais?.transporte?.transportadorInternacional?.transportador
                      : null,
                    pais: atributosAdicionais?.transporte?.transportadorInternacional?.pais?.id
                      ? atributosAdicionais?.transporte?.transportadorInternacional?.pais
                      : null,
                  }
                : null,
            portoAeroporto: atributosAdicionais?.transporte?.portoAeroporto?.id
              ? atributosAdicionais?.transporte?.portoAeroporto
              : null,
          },
          carga: {
            ...atributosAdicionais?.carga,
            armazens: normalizedArmazens,
            agenteTransportador: atributosAdicionais?.carga?.agenteTransportador?.id
              ? atributosAdicionais?.carga?.agenteTransportador
              : null,
            setorLotacao: atributosAdicionais?.carga?.setorLotacao?.id
              ? atributosAdicionais?.carga?.setorLotacao
              : null,
            recintoAduaneiro: atributosAdicionais?.carga?.recintoAduaneiro?.id
              ? atributosAdicionais?.carga?.recintoAduaneiro
              : null,
            quantidades: normalizedQuantidades,
            declaracaoExportacoes: normalizedDeclaracaoExportacoes,
          },
          adquirente: atributosAdicionais?.adquirente?.id ? atributosAdicionais?.adquirente : null,
          adicoes: normalizedAdicoes,
          documentosAnexacaoValor: _.isArray(atributosAdicionais?.documentosAnexacaoValor)
            ? atributosAdicionais?.documentosAnexacaoValor?.map((documento) => {
                if (validate(documento?.id)) {
                  return {
                    ...documento,
                    id: null,
                  };
                }
                return documento;
              })
            : [],
        },
        mle: {
          ...mle,
          moeda: mle?.moeda?.id ? mle?.moeda : null,
          valorMoeda: normalizeNumeral(mle?.valorMoeda),
          valorReal: normalizeNumeral(mle?.valorReal),
        },
        desconto: {
          ...desconto,
          moeda: desconto?.moeda?.id ? desconto?.moeda : null,
          valorMoeda: normalizeNumeral(desconto?.valorMoeda),
          valorReal: normalizeNumeral(desconto?.valorReal),
        },
        despesa: {
          ...despesa,
          moeda: despesa?.moeda?.id ? despesa?.moeda : null,
          valorMoeda: normalizeNumeral(despesa?.valorMoeda),
          valorReal: normalizeNumeral(despesa?.valorReal),
        },
        frete: {
          ...frete,
          moeda: frete?.moeda?.id ? frete?.moeda : null,
          valorMoedaCollect: normalizeNumeral(frete?.valorMoedaCollect),
          valorRealCollect: normalizeNumeral(frete?.valorRealCollect),
          valorMoedaPrepaid: normalizeNumeral(frete?.valorMoedaPrepaid),
          valorRealPrepaid: normalizeNumeral(frete?.valorRealPrepaid),
          valorFreteNacionalMoeda: normalizeNumeral(frete?.valorFreteNacionalMoeda),
          valorFreteNacionalReal: normalizeNumeral(frete?.valorFreteNacionalReal),
          valorMoedaPrepaidOriginal: normalizeNumeral(frete?.valorMoedaPrepaidOriginal),
          valorRealPrepaidOriginal: normalizeNumeral(frete?.valorRealPrepaidOriginal),
          valorMoedaCollectOriginal: normalizeNumeral(frete?.valorMoedaCollectOriginal),
          valorRealCollectOriginal: normalizeNumeral(frete?.valorRealCollectOriginal),
        },
        seguro: {
          ...seguro,
          moeda: seguro?.moeda?.id ? seguro?.moeda : null,
          valorMoeda: normalizeNumeral(seguro?.valorMoeda),
          valorReal: normalizeNumeral(seguro?.valorReal),
          valorPercentual: normalizeNumeral(seguro?.valorPercentual),
        },
        totaisAfrmm: normalizedAfmm,
        despesasAcessorias: normalizedDespesasAcessorias,
        exportador: exportador?.id ? exportador : null,
        fabricante: fabricante?.id ? fabricante : null,
        paisOrigem: paisOrigem?.id ? paisOrigem : null,
        mercadorias: normalizedMercadorias,
      };

      return normalizedData;
    },
    [validate, normalizeData, normalizeNumeral]
  );

  const formataPagamentosRetificados = (list) => {
    const pagamentosClassificadosObj = {};
    const pagamentosClassificadosArr = [];

    list.forEach((pagamento) => {
      if (!pagamentosClassificadosObj[pagamento.identificacao]) {
        pagamentosClassificadosObj[pagamento.identificacao] = [];
      }

      pagamentosClassificadosObj[pagamento.identificacao].push(pagamento);
    });

    console.log(pagamentosClassificadosObj);

    const listIdentificacoes = Object.keys(pagamentosClassificadosObj);

    listIdentificacoes.forEach((key) => {
      const pagamentoObj = {
        id: null,
        identificacao: `${key}`,
        idRetificacao: null,
        valorOriginal: 0,
        valorRetificacao: 0,
        valorDiferenca: 0,
      };

      pagamentosClassificadosObj[key].forEach((p) => {
        if (p.isRetificacao) {
          pagamentoObj.idRetificacao = p.id;
          pagamentoObj.valorRetificacao = p.valorReal;
        } else {
          pagamentoObj.id = p.id;
          pagamentoObj.valorOriginal = p.valorReal;
        }
      });

      if (pagamentoObj.valorOriginal !== 0 && pagamentoObj.valorDiferenca !== 0) {
        pagamentoObj.valorDiferenca = pagamentoObj.valorOriginal - pagamentoObj.valorDiferenca;
      }

      pagamentosClassificadosArr.push(pagamentoObj);
    });

    return pagamentosClassificadosArr;
  };

  const unnormalize = useCallback(
    (normalizedData) => {
      const {
        pesoLiquido,
        pesoBruto,
        data,
        atributosAdicionais,
        mle,
        desconto,
        despesa,
        frete,
        seguro,
        mercadorias,
        lis,
        totaisAfrmm,
        followUp,
        despesasAcessorias,
      } = normalizedData;

      const unnormalizedAdicoes = atributosAdicionais?.adicoes || [];

      const normalizedPagamentos = (atributosAdicionais?.pagamentos || [])?.map((pagamento) =>
        validate(pagamento?.id)
          ? {
              ...pagamento,
              id: null,
              valorReal: isValid(pagamento?.valorReal)
                ? unnormalizeNumeral(pagamento?.valorReal, formatBrazilianNumericDecimal(2))
                : undefined,
              codigoReceita: pagamento?.codigoReceita?.id ? pagamento?.codigoReceita : null,
              novoCodigoReceita: pagamento?.novoCodigoReceita?.id ? pagamento?.novoCodigoReceita : null,
            }
          : {
              ...pagamento,
              valorReal: isValid(pagamento?.valorReal)
                ? unnormalizeNumeral(pagamento?.valorReal, formatBrazilianNumericDecimal(2))
                : undefined,
              codigoReceita: pagamento?.codigoReceita?.id ? pagamento?.codigoReceita : null,
              novoCodigoReceita: pagamento?.novoCodigoReceita?.id ? pagamento?.novoCodigoReceita : null,
            }
      );
      normalizedPagamentos.sort((a, b) => {
        if (a?.codigoReceita?.code > b?.codigoReceita?.code) {
          return 1;
        }
        if (a?.codigoReceita?.code < b?.codigoReceita?.code) {
          return -1;
        }
        return 0;
      });
      const normalizedAcrescimosAduaneiros = atributosAdicionais?.acrescimosDeducoes || [];

      const normalizedMercadorias = mercadorias || [];

      const normalizedLis = lis || [];

      const normalizedQuantidades = atributosAdicionais?.carga?.quantidades || [];

      const normalizedDeclaracaoExportacoes = atributosAdicionais?.carga?.declaracaoExportacoes || [];

      const unnormalizedAfrmm = {
        ...totaisAfrmm,
        valorDolar: unnormalizeNumeral(totaisAfrmm?.valorDolar, formatBrazilianNumericDecimal(2)),
        valorReal: unnormalizeNumeral(totaisAfrmm?.valorReal, formatBrazilianNumericDecimal(2)),
        valorMoeda: unnormalizeNumeral(totaisAfrmm?.valorMoeda, formatBrazilianNumericDecimal(2)),
      };

      const unnormalizedDespesasAcessorias = {
        ...despesasAcessorias,
        valorReal: unnormalizeNumeral(despesasAcessorias?.valorReal, formatBrazilianNumericDecimal(2)),
        valorMoeda: unnormalizeNumeral(despesasAcessorias?.valorMoeda, formatBrazilianNumericDecimal(2)),
      };

      const unnormalizedComponentesFrete = (atributosAdicionais?.componentesFrete ?? [])?.map(
        (normalizedComponenteFrete) => ({
          ...normalizedComponenteFrete,
          valor: unnormalizeNumeral(normalizedComponenteFrete?.valor, formatBrazilianNumericDecimal(2)),
          valorReal: unnormalizeNumeral(normalizedComponenteFrete?.valorReal, formatBrazilianNumericDecimal(2)),
        })
      );

      const unnormalizedPagamentos = normalizedPagamentos.map((pagamento) => ({
        ...pagamento,
        valor: isValid(pagamento?.valor)
          ? unnormalizeNumeral(pagamento?.valor, formatBrazilianNumericDecimal(2))
          : undefined,
        valorSiscomex: isValid(pagamento?.valorSiscomex)
          ? unnormalizeNumeral(pagamento?.valorSiscomex, formatBrazilianNumericDecimal(2))
          : undefined,
      }));

      return {
        ...normalizedData,
        pesoLiquido: isValid(pesoLiquido)
          ? forceUnnormalizeNumeral(pesoLiquido, formatBrazilianNumericDecimal(5))
          : undefined,
        pesoBruto: isValid(pesoBruto)
          ? forceUnnormalizeNumeral(pesoBruto, formatBrazilianNumericDecimal(5))
          : undefined,
        data: unnormalizeData(data),
        atributosAdicionais: {
          ...atributosAdicionais,
          componentesFrete: unnormalizedComponentesFrete,
          acrescimosDeducoes: normalizedAcrescimosAduaneiros.map((acrescimoAduaneiro) => ({
            ...acrescimoAduaneiro,
            valorMoeda: isValid(acrescimoAduaneiro?.valorMoeda)
              ? unnormalizeNumeral(acrescimoAduaneiro?.valorMoeda, formatBrazilianNumericDecimal(2))
              : undefined,
            valorReal: isValid(acrescimoAduaneiro?.valorReal)
              ? unnormalizeNumeral(acrescimoAduaneiro?.valorReal, formatBrazilianNumericDecimal(2))
              : undefined,
            acrescimoValoracaoAduaneira: !acrescimoAduaneiro?.acrescimoValoracaoAduaneira
              ? acrescimoAduaneiro?.deducaoValoracaoAduaneira
              : acrescimoAduaneiro?.acrescimoValoracaoAduaneira,
          })),
          adicoes: unnormalizedAdicoes,
          pagamentos: unnormalizedPagamentos,
          transporte: {
            ...atributosAdicionais?.transporte,
            dataEmbarque: unnormalizeData(atributosAdicionais?.transporte?.dataEmbarque),
            dataPostagem: unnormalizeData(atributosAdicionais?.transporte?.dataPostagem),
          },
          carga: {
            ...atributosAdicionais?.carga,
            armazens: (atributosAdicionais?.carga?.armazens ?? []).map((armazem) => ({ id: uuid(), value: armazem })),
            quantidades: normalizedQuantidades.map((quantidade) => ({
              ...quantidade,
              quantidade: isValid(quantidade?.quantidade)
                ? unnormalizeNumeral(quantidade?.quantidade, formatBrazilianNumericDecimalWithoutSeparator(0))
                : undefined,
            })),
            declaracaoExportacoes: normalizedDeclaracaoExportacoes.map((cde) => ({
              ...cde,
              number: cde.numero,
              start: cde.inicio,
              end: cde.fim,
            })),
          },
        },
        mle: {
          ...mle,
          valorMoeda: isValid(mle?.valorMoeda)
            ? unnormalizeNumeral(mle?.valorMoeda, formatBrazilianNumericDecimal(2))
            : undefined,
          valorReal: isValid(mle?.valorReal)
            ? unnormalizeNumeral(mle?.valorReal, formatBrazilianNumericDecimal(2))
            : undefined,
        },
        desconto: {
          ...desconto,
          moeda: desconto?.moeda?.id ? desconto?.moeda : undefined,
          valorMoeda: isValid(desconto?.valorMoeda)
            ? unnormalizeNumeral(desconto?.valorMoeda, formatBrazilianNumericDecimal(2))
            : undefined,
          valorReal: isValid(desconto?.valorReal)
            ? unnormalizeNumeral(desconto?.valorReal, formatBrazilianNumericDecimal(2))
            : undefined,
        },
        despesa: {
          ...despesa,
          valorMoeda: isValid(despesa?.valorMoeda)
            ? unnormalizeNumeral(despesa?.valorMoeda, formatBrazilianNumericDecimal(2))
            : undefined,
          valorReal: isValid(despesa?.valorReal)
            ? unnormalizeNumeral(despesa?.valorReal, formatBrazilianNumericDecimal(2))
            : undefined,
        },
        frete: {
          ...frete,
          moeda: frete?.moeda?.id ? frete?.moeda : undefined,
          valorMoedaPrepaid: isValid(frete?.valorMoedaPrepaid)
            ? unnormalizeNumeral(frete?.valorMoedaPrepaid, formatBrazilianNumericDecimal(2))
            : undefined,
          valorMoedaPrepaidOriginal: isValid(frete?.valorMoedaPrepaidOriginal)
            ? unnormalizeNumeral(frete?.valorMoedaPrepaidOriginal, formatBrazilianNumericDecimal(2))
            : undefined,
          valorRealPrepaid: isValid(frete?.valorRealPrepaid)
            ? unnormalizeNumeral(frete?.valorRealPrepaid, formatBrazilianNumericDecimal(2))
            : undefined,
          valorRealPrepaidOriginal: isValid(frete?.valorRealPrepaidOriginal)
            ? unnormalizeNumeral(frete?.valorRealPrepaidOriginal, formatBrazilianNumericDecimal(2))
            : undefined,
          valorFreteNacionalMoeda: isValid(frete?.valorFreteNacionalMoeda)
            ? unnormalizeNumeral(frete?.valorFreteNacionalMoeda, formatBrazilianNumericDecimal(2))
            : undefined,
          valorFreteNacionalReal: isValid(frete?.valorFreteNacionalReal)
            ? unnormalizeNumeral(frete?.valorFreteNacionalReal, formatBrazilianNumericDecimal(2))
            : undefined,
          valorMoedaCollect: isValid(frete?.valorMoedaCollect)
            ? unnormalizeNumeral(frete?.valorMoedaCollect, formatBrazilianNumericDecimal(2))
            : undefined,
          valorMoedaCollectOriginal: isValid(frete?.valorMoedaCollectOriginal)
            ? unnormalizeNumeral(frete?.valorMoedaCollectOriginal, formatBrazilianNumericDecimal(2))
            : undefined,
          valorRealCollect: isValid(frete?.valorRealCollect)
            ? unnormalizeNumeral(frete?.valorRealCollect, formatBrazilianNumericDecimal(2))
            : undefined,
          valorRealCollectOriginal: isValid(frete?.valorRealCollectOriginal)
            ? unnormalizeNumeral(frete?.valorRealCollectOriginal, formatBrazilianNumericDecimal(2))
            : undefined,
          valorMoedaFreteComponentesTotal:
            isValid(frete?.valorMoedaPrepaid) || isValid(frete?.valorMoedaCollect)
              ? unnormalizeNumeral(
                  (frete?.valorMoedaPrepaid ?? 0) + (frete?.valorMoedaCollect ?? 0),
                  formatBrazilianNumericDecimal(2)
                )
              : undefined,
          valorRealFreteComponentesTotal:
            isValid(frete?.valorRealPrepaid) || isValid(frete?.valorRealCollect)
              ? unnormalizeNumeral(
                  (frete?.valorRealPrepaid ?? 0) + (frete?.valorRealCollect ?? 0),
                  formatBrazilianNumericDecimal(2)
                )
              : undefined,
        },
        seguro: {
          ...seguro,
          moeda: seguro?.moeda?.id ? seguro?.moeda : undefined,
          valorMoeda: isValid(seguro?.valorMoeda)
            ? unnormalizeNumeral(seguro?.valorMoeda, formatBrazilianNumericDecimal(2))
            : undefined,
          valorReal: isValid(seguro?.valorReal)
            ? unnormalizeNumeral(seguro?.valorReal, formatBrazilianNumericDecimal(2))
            : undefined,
          valorPercentual: isValid(seguro?.valorPercentual)
            ? unnormalizeNumeral(seguro?.valorPercentual, formatBrazilianNumericDecimal(7))
            : undefined,
          parametroCalcSeguro:
            normalizedData?.seguro?.parametroCalcSeguro ?? normalizedData?.importador?.pessoa?.parametroCalcSeguro,
        },
        totaisAfrmm: unnormalizedAfrmm,
        despesasAcessorias: unnormalizedDespesasAcessorias,
        mercadorias: normalizedMercadorias,
        lis: normalizedLis,
        ignorableFields: {
          pagamentos: formataPagamentosRetificados(unnormalizedPagamentos),
        },
      };
    },
    [isValid, unnormalizeData, unnormalizeNumeral, formatBrazilianNumericDecimal]
  );

  const normalizeAuxiliar = useCallback((unnormalizedData) => {
    const mapAcrecimoDeducao = (acrecimoDeducao) => {
      const isUUid = validate(acrecimoDeducao?.id);

      if (isUUid) {
        const obj = {
          ...acrecimoDeducao,
          id: null,
          valorMoeda: normalizeNumeral(acrecimoDeducao?.valorMoeda),
          valorReal: normalizeNumeral(acrecimoDeducao?.valorReal),
          moeda: acrecimoDeducao?.moeda?.id ? acrecimoDeducao?.moeda : null,
          acrescimoValoracaoAduaneira:
            acrecimoDeducao?.acrescimoValoracaoAduaneira?.id && acrecimoDeducao?.tipo === TIPO_ACRESCIMO
              ? acrecimoDeducao?.acrescimoValoracaoAduaneira
              : null,
          deducaoValoracaoAduaneira:
            acrecimoDeducao?.acrescimoValoracaoAduaneira?.id && acrecimoDeducao?.tipo === TIPO_DEDUCAO
              ? acrecimoDeducao?.acrescimoValoracaoAduaneira
              : null,
        };
        return obj;
      }

      const obj = {
        ...acrecimoDeducao,
        valorMoeda: normalizeNumeral(acrecimoDeducao?.valorMoeda),
        valorReal: normalizeNumeral(acrecimoDeducao?.valorReal),
        moeda: acrecimoDeducao?.moeda?.id ? acrecimoDeducao?.moeda : null,
        acrescimoValoracaoAduaneira:
          acrecimoDeducao?.acrescimoValoracaoAduaneira?.id && acrecimoDeducao?.tipo === TIPO_ACRESCIMO
            ? acrecimoDeducao?.acrescimoValoracaoAduaneira
            : null,
        deducaoValoracaoAduaneira:
          acrecimoDeducao?.acrescimoValoracaoAduaneira?.id && acrecimoDeducao?.tipo === TIPO_DEDUCAO
            ? acrecimoDeducao?.acrescimoValoracaoAduaneira
            : null,
      };

      return obj;
    };
    const normalizedAcrescimosDeducoes = (unnormalizedData?.acrescimosDeducoes || [])?.map(mapAcrecimoDeducao);

    const adicaoNormalizada = {
      ...unnormalizedData,
      relacao: unnormalizedData?.relacao,
      coberturaCambial: unnormalizedData?.coberturaCambial,
      condicaoMercadoria: unnormalizedData?.condicaoMercadoria,
      aplicacaoMercadoria: unnormalizedData?.aplicacaoMercadoria,
      numeroRof: unnormalizedData?.numeroRof,
      destaqueNcm: unnormalizedData?.destaqueNcm,
      quebraAdicaoCfop: parseToBoolean(unnormalizedData?.quebraAdicaoCfop),
      quebraAdicaoNumeroFatura: parseToBoolean(unnormalizedData?.quebraAdicaoNumeroFatura),
      exportador: unnormalizedData?.exportador?.id ? unnormalizedData?.exportador : undefined,
      fabricante: unnormalizedData?.fabricante?.id ? unnormalizedData?.fabricante : undefined,
      paisOrigem: unnormalizedData?.paisOrigem?.id ? unnormalizedData?.paisOrigem : undefined,
      instituicaoFinanciadora: unnormalizedData?.instituicaoFinanciadora?.id
        ? unnormalizedData?.instituicaoFinanciadora
        : undefined,
      motivoImportacaoSemCoberturaCambial: unnormalizedData?.motivoImportacaoSemCoberturaCambial?.id
        ? unnormalizedData?.motivoImportacaoSemCoberturaCambial
        : undefined,
      moeda: unnormalizedData?.moeda?.id ? unnormalizedData?.moeda : null,
      moedaDespesa: unnormalizedData?.moedaDespesa?.id ? unnormalizedData?.moedaDespesa : null,
      ncm: unnormalizedData?.ncm?.id ? unnormalizedData?.ncm : null,
      naladiNcca: unnormalizedData?.naladiNcca?.id ? unnormalizedData?.naladiNcca : null,
      naladiSh: unnormalizedData?.naladiSh?.id ? unnormalizedData?.naladiSh : null,
      operacaoFiscal: unnormalizedData?.operacaoFiscal?.id ? unnormalizedData?.operacaoFiscal : null,
      atributos: (unnormalizedData?.atributos || [])?.map((atributo) =>
        validate(atributo?.id)
          ? {
              ...atributo,
              nve: atributo?.nve?.id ? atributo?.nve : null,
              atributo: atributo?.atributo?.id ? atributo?.atributo : null,
              especificacao: atributo?.especificacao?.id ? atributo?.especificacao : null,
              id: null,
            }
          : {
              ...atributo,
              nve: atributo?.nve?.id ? atributo?.nve : null,
              atributo: atributo?.atributo?.id ? atributo?.atributo : null,
              especificacao: atributo?.especificacao?.id ? atributo?.especificacao : null,
            }
      ),
      declaracaoExportacoes: (unnormalizedData?.declaracaoExportacoes || [])?.map((de) =>
        validate(de?.id)
          ? {
              ...de,
              id: null,
              qtdUnidadeEstatistica: normalizeNumeral(de?.qtdUnidadeEstatistica),
            }
          : {
              ...de,
              qtdUnidadeEstatistica: normalizeNumeral(de?.qtdUnidadeEstatistica),
            }
      ),
      valorFornecedor: normalizeNumeral(unnormalizedData?.valorFornecedor),
      pesoLiquido: normalizeNumeral(unnormalizedData?.pesoLiquido),
      pesoBruto: normalizeNumeral(unnormalizedData?.pesoBruto),
      quantidadeEstatistica: normalizeNumeral(unnormalizedData?.quantidadeEstatistica),
      valorMleReal: normalizeNumeral(unnormalizedData?.valorMleReal),
      valorMleMoeda: normalizeNumeral(unnormalizedData?.valorMleMoeda),
      valorDespesaMoeda: normalizeNumeral(unnormalizedData?.valorDespesaMoeda),
      valorDespesaReal: normalizeNumeral(unnormalizedData?.valorDespesaReal),
      valorCifReal: normalizeNumeral(unnormalizedData?.valorCifReal),
      valorMcvMoeda: normalizeNumeral(unnormalizedData?.valorMcvMoeda),
      valorMcvReal: normalizeNumeral(unnormalizedData?.valorMcvReal),
      valorFreteTerritorioNacionalMoeda: normalizeNumeral(unnormalizedData?.valorFreteTerritorioNacionalMoeda),
      valorFreteTerritorioNacionalReal: normalizeNumeral(unnormalizedData?.valorFreteTerritorioNacionalReal),
      valorSeguroMoeda: normalizeNumeral(unnormalizedData?.valorSeguroMoeda),
      valorSeguroReal: normalizeNumeral(unnormalizedData?.valorSeguroReal),
      valorFreteMoeda: normalizeNumeral(unnormalizedData?.valorFreteMoeda),
      valorFreteReal: normalizeNumeral(unnormalizedData?.valorFreteReal),
      acrescimosDeducoes: normalizedAcrescimosDeducoes,
      incotermCondicaoVenda: unnormalizedData?.incotermCondicaoVenda?.id
        ? unnormalizedData?.incotermCondicaoVenda
        : null,
      metodoValoracaoAduaneira: unnormalizedData?.metodoValoracaoAduaneira?.id
        ? unnormalizedData?.metodoValoracaoAduaneira
        : null,
      portoAeroporto: unnormalizedData?.portoAeroporto?.id ? unnormalizedData?.portoAeroporto : null,
      ii: {
        ...unnormalizedData?.ii,
        fundamentoLegalRegimeTributacaoIi: unnormalizedData?.ii?.fundamentoLegalRegimeTributacaoIi?.id
          ? unnormalizedData?.ii?.fundamentoLegalRegimeTributacaoIi
          : null,
        tipoAtoLegalExTarifario: unnormalizedData?.ii?.tipoAtoLegalExTarifario?.id
          ? unnormalizedData?.ii?.tipoAtoLegalExTarifario
          : null,
        tipoAtoLegalAcordoTarifario: unnormalizedData?.ii?.tipoAtoLegalAcordoTarifario?.id
          ? unnormalizedData?.ii?.tipoAtoLegalAcordoTarifario
          : null,
        regimeTributacao: unnormalizedData?.ii?.regimeTributacao?.id ? unnormalizedData?.ii?.regimeTributacao : null,
        orgaoEmissorAtoLegalExTarifario: unnormalizedData?.ii?.orgaoEmissorAtoLegalExTarifario?.id
          ? unnormalizedData?.ii?.orgaoEmissorAtoLegalExTarifario
          : null,
        orgaoEmissorAtoLegalAcordoTarifario: unnormalizedData?.ii?.orgaoEmissorAtoLegalAcordoTarifario?.id
          ? unnormalizedData?.ii?.orgaoEmissorAtoLegalAcordoTarifario
          : undefined,
        ano: normalizeYear(unnormalizedData?.ii?.ano),
        anoExTarifario: normalizeYear(unnormalizedData?.ii?.anoExTarifario),
        anoAcordoTarifario: normalizeYear(unnormalizedData?.ii?.anoAcordoTarifario),
        baseCalculoReal: normalizeNumeral(unnormalizedData?.ii?.baseCalculoReal),
        aliquota: normalizeNumeral(unnormalizedData?.ii?.aliquota),
        aliquotaAcordo: normalizeNumeral(unnormalizedData?.ii?.aliquotaAcordo),
        percentualReducao: normalizeNumeral(unnormalizedData?.ii?.percentualReducao),
        aliquotaReduzida: normalizeNumeral(unnormalizedData?.ii?.aliquotaReduzida),
        coeficienteReducao: normalizeNumeral(unnormalizedData?.ii?.coeficienteReducao),
        valorDolar: normalizeNumeral(unnormalizedData?.ii?.valorDolar),
        valorReal: normalizeNumeral(unnormalizedData?.ii?.valorReal),
        valorDevidoReal: normalizeNumeral(unnormalizedData?.ii?.valorDevidoReal),
        valorRecolherReal: normalizeNumeral(unnormalizedData?.ii?.valorRecolherReal),
        recolherPermanencia: normalizeNumeral(unnormalizedData?.ii?.recolherPermanencia),
        ...(unnormalizedData?.ii?.acordoAladi && {
          acordoAladi:
            typeof unnormalizedData?.ii?.acordoAladi === 'number'
              ? { id: unnormalizedData?.ii?.acordoAladi }
              : unnormalizedData?.ii?.acordoAladi,
        }),
      },
      ipi: {
        ...unnormalizedData?.ipi,
        tipoAtoLegalFundamentoLegal: unnormalizedData?.ipi?.tipoAtoLegalFundamentoLegal?.id
          ? unnormalizedData?.ipi?.tipoAtoLegalFundamentoLegal
          : null,
        orgaoEmissorFundamentoLegal: unnormalizedData?.ipi?.orgaoEmissorFundamentoLegal?.id
          ? unnormalizedData?.ipi?.orgaoEmissorFundamentoLegal
          : null,
        numeroAtoFundamentoLegal: unnormalizedData?.ipi?.numeroAtoFundamentoLegal,
        anoFundamentoLegal: normalizeYear(unnormalizedData?.ipi?.anoFundamentoLegal),
        numeroExFundamentoLegal: unnormalizedData?.ipi?.numeroExFundamentoLegal,
        orgaoEmissorAtoLegal: unnormalizedData?.ipi?.orgaoEmissorAtoLegal?.id
          ? unnormalizedData?.ipi?.orgaoEmissorAtoLegal
          : null,
        regimeTributacao: unnormalizedData?.ipi?.regimeTributacao?.id ? unnormalizedData?.ipi?.regimeTributacao : null,
        tipoRecipiente: unnormalizedData?.ipi?.tipoRecipiente?.id ? unnormalizedData?.ipi?.tipoRecipiente : null,
        unidadeDeMedida: unnormalizedData?.ipi?.unidadeDeMedida?.id ? unnormalizedData?.ipi?.unidadeDeMedida : null,
        tipoAtoLegalExTarifario: unnormalizedData?.ipi?.tipoAtoLegalExTarifario?.id
          ? unnormalizedData?.ipi?.tipoAtoLegalExTarifario
          : null,

        baseCalculoReal: normalizeNumeral(unnormalizedData?.ipi?.baseCalculoReal),
        aliquota: normalizeNumeral(unnormalizedData?.ipi?.aliquota),
        aliquotaReduzida: normalizeNumeral(unnormalizedData?.ipi?.aliquotaReduzida),
        valorUnidadeMedidaReal: normalizeNumeral(unnormalizedData?.ipi?.valorUnidadeMedidaReal),
        valorRecolherReal: normalizeNumeral(unnormalizedData?.ipi?.valorRecolherReal),
        recolherPermanencia: normalizeNumeral(unnormalizedData?.ipi?.recolherPermanencia),
        valorDevidoReal: normalizeNumeral(unnormalizedData?.ipi?.valorDevidoReal),
        quantidade: normalizeNumeral(unnormalizedData?.ipi?.quantidade),
        capacidade: normalizeNumeral(unnormalizedData?.ipi?.capacidade),
        anoExTarifario: normalizeYear(unnormalizedData?.ipi?.anoExTarifario),
        ipiNaoTributavel: parseToBoolean(unnormalizedData?.ipi?.ipiNaoTributavel),
        notaComplementarTipi: unnormalizedData?.ipi?.notaComplementarTipi,
        reduzida: normalizeNumeral(unnormalizedData?.ipi?.reduzida),
      },
      multaLi: {
        ...unnormalizedData?.multaLi,
        valorDevidoReal: normalizeNumeral(unnormalizedData?.multaLi?.valorDevidoReal),
        valorRecolherReal: normalizeNumeral(unnormalizedData?.multaLi?.valorRecolherReal),
      },
      pisCofins: {
        ...unnormalizedData?.pisCofins,
        unidadeDeMedidaPisPasep: unnormalizedData?.pisCofins?.unidadeDeMedidaPisPasep?.id
          ? unnormalizedData?.pisCofins?.unidadeDeMedidaPisPasep
          : null,
        unidadeDeMedidaCofins: unnormalizedData?.pisCofins?.unidadeDeMedidaCofins?.id
          ? unnormalizedData?.pisCofins?.unidadeDeMedidaCofins
          : null,
        regimeTributacao: unnormalizedData?.pisCofins?.regimeTributacao?.id
          ? unnormalizedData?.pisCofins?.regimeTributacao
          : null,
        fundamentoLegalRegimeTributacaoPisCofins: unnormalizedData?.pisCofins?.fundamentoLegalRegimeTributacaoPisCofins
          ?.id
          ? unnormalizedData?.pisCofins?.fundamentoLegalRegimeTributacaoPisCofins
          : null,
        fundamentoLegal: unnormalizedData?.pisCofins?.fundamentoLegal?.id
          ? unnormalizedData?.pisCofins?.fundamentoLegal
          : null,
        baseCalculoReal: normalizeNumeral(unnormalizedData?.pisCofins?.baseCalculoReal),
        percentualReducao: normalizeNumeral(unnormalizedData?.pisCofins?.percentualReducao),
        percentualReducaoBaseCalculo: normalizeNumeral(unnormalizedData?.pisCofins?.percentualReducaoBaseCalculo),
        aliquotaPisPasep: normalizeNumeral(unnormalizedData?.pisCofins?.aliquotaPisPasep),
        aliquotaReduzidaPisPasep: normalizeNumeral(unnormalizedData?.pisCofins?.aliquotaReduzidaPisPasep),
        valorRecolherPisPasepReal: normalizeNumeral(unnormalizedData?.pisCofins?.valorRecolherPisPasepReal),
        valorDevidoCofinsReal: normalizeNumeral(unnormalizedData?.pisCofins?.valorDevidoCofinsReal),
        valorDevidoPisPasepReal: normalizeNumeral(unnormalizedData?.pisCofins?.valorDevidoPisPasepReal),
        recolherPermanenciaPisPasep: normalizeNumeral(unnormalizedData?.pisCofins?.recolherPermanenciaPisPasep),
        recolherPermanenciaCofins: normalizeNumeral(unnormalizedData?.pisCofins?.recolherPermanenciaCofins),
        quantidadePisPasep: normalizeNumeral(unnormalizedData?.pisCofins?.quantidadePisPasep),
        aliquotaCofins: normalizeNumeral(unnormalizedData?.pisCofins?.aliquotaCofins),
        aliquotaReduzidaCofins: normalizeNumeral(unnormalizedData?.pisCofins?.aliquotaReduzidaCofins),
        valorRecolherCofinsReal: normalizeNumeral(unnormalizedData?.pisCofins?.valorRecolherCofinsReal),
        quantidadeCofins: normalizeNumeral(unnormalizedData?.pisCofins?.quantidadeCofins),
      },
      taxaSiscomex: {
        ...unnormalizedData?.taxaSiscomex,
        valorReal: normalizeNumeral(unnormalizedData?.taxaSiscomex?.valorReal),
        valorDolar: normalizeNumeral(unnormalizedData?.taxaSiscomex?.valorDolar),
        valorMoeda: normalizeNumeral(unnormalizedData?.taxaSiscomex?.valorMoeda),
      },
      icms: {
        ...unnormalizedData?.icms,
        regimeTributacao: unnormalizedData?.icms?.regimeTributacao?.id
          ? unnormalizedData?.icms?.regimeTributacao
          : null,
        dispositivoLegal: unnormalizedData?.icms?.dispositivoLegal?.id
          ? unnormalizedData?.icms?.dispositivoLegal
          : null,
        baseCalculoReal: normalizeNumeral(unnormalizedData?.icms?.baseCalculoReal),
        aliquota: normalizeNumeral(unnormalizedData?.icms?.aliquota),
        aliquotaAntecipadaPercentual: normalizeNumeral(unnormalizedData?.icms?.aliquotaAntecipadaPercentual),
        aliquotaOriginal: normalizeNumeral(unnormalizedData?.icms?.aliquotaOriginal),
        aliquotaFecp: normalizeNumeral(unnormalizedData?.icms?.aliquotaFecp),
        percentualReducao: normalizeNumeral(unnormalizedData?.icms?.percentualReducao),
        valorRecolherReal: normalizeNumeral(unnormalizedData?.icms?.valorRecolherReal),
        recolherPermanencia: normalizeNumeral(unnormalizedData?.icms?.recolherPermanencia),
        valorDevidoReal: normalizeNumeral(unnormalizedData?.icms?.valorDevidoReal),
        percentualBeneficio: normalizeNumeral(unnormalizedData?.icms?.percentualBeneficio),
        tipoReducao: unnormalizedData?.icms?.tipoReducao,
      },
      antidumping: {
        ...unnormalizedData?.antidumping,
        orgaoEmissorAtoLegal: unnormalizedData?.antidumping?.orgaoEmissorAtoLegal?.id
          ? unnormalizedData?.antidumping?.orgaoEmissorAtoLegal
          : null,
        unidadeDeMedida: unnormalizedData?.antidumping?.unidadeDeMedida?.id
          ? unnormalizedData?.antidumping?.unidadeDeMedida
          : null,
        tipoAtoLegal: unnormalizedData?.antidumping?.tipoAtoLegal?.id
          ? unnormalizedData?.antidumping?.tipoAtoLegal
          : null,
        ano: normalizeYear(unnormalizedData?.antidumping?.ano),
        aliquota: normalizeNumeral(unnormalizedData?.antidumping?.aliquota),
        valorReal: normalizeNumeral(unnormalizedData?.antidumping?.valorReal),
        quantidade: normalizeNumeral(unnormalizedData?.antidumping?.quantidade),
        baseCalculoReal: normalizeNumeral(unnormalizedData?.antidumping?.baseCalculoReal),
        valorRecolherReal: normalizeNumeral(unnormalizedData?.antidumping?.valorRecolherReal),
        valorDevidoReal: normalizeNumeral(unnormalizedData?.antidumping?.valorDevidoReal),
      },
      afrmm: {
        ...unnormalizedData?.afrmm,
        valorRecolherReal: normalizeNumeral(unnormalizedData?.afrmm?.valorRecolherReal),
        valorDevidoReal: normalizeNumeral(unnormalizedData?.afrmm?.valorDevidoReal),
      },
      mercadorias: unnormalizedData?.mercadorias || [],
    };

    // normaliza atributos de fundamento legal da duimp
    const atributosNormalizadosCat = normalizeAtributosDinamicosNcm(unnormalizedData?.atributosFundamentoLegalDuimp); // essa funcao normaliza para o catalogo, necessario adaptar

    if (atributosNormalizadosCat && atributosNormalizadosCat.atributosSimples) {
      adicaoNormalizada.atributosSimplesFundamentoLegalDuimp = atributosNormalizadosCat.atributosSimples;
    }

    if (adicaoNormalizada && adicaoNormalizada.atributosPreenchidos) delete adicaoNormalizada.atributosPreenchidos;
    if (adicaoNormalizada && adicaoNormalizada.atributosFundamentoLegalDuimp)
      delete adicaoNormalizada.atributosFundamentoLegalDuimp;

    // normaliza atributos tratamento tributario
    if (
      unnormalizedData?.ncm?.tratamentosTributarios &&
      Array.isArray(unnormalizedData?.ncm?.tratamentosTributarios) &&
      unnormalizedData?.ncm?.tratamentosTributarios.length
    ) {
      const atributosCodes = [];

      unnormalizedData?.ncm?.tratamentosTributarios.forEach((tratamento) => {
        if (tratamento && tratamento?.listaAtributosMetadados && Array.isArray(tratamento?.listaAtributosMetadados)) {
          tratamento.listaAtributosMetadados.forEach((el) => {
            atributosCodes.push(el.codigo);
          });
        }
      });

      const atributosTratamento = unnormalizedData?.atributosTratamentoTributario?.atributosPreenchidos?.atributos;
      let atributos = [];

      if (atributosTratamento && _.isObject(atributosTratamento)) atributos = Object.keys(atributosTratamento);

      const atributosNcmAtual = {};

      if (atributos && atributos.length) {
        atributos.forEach((atributoAtual) => {
          if (atributosCodes && atributosCodes.find((el) => el === atributoAtual)) {
            atributosNcmAtual[atributoAtual] = {};
            atributosNcmAtual[atributoAtual] = atributosTratamento[atributoAtual];
          }
        });

        if (Object.keys(atributosNcmAtual).length) {
          unnormalizedData.atributosTratamentoTributario.atributosPreenchidos.atributos = atributosNcmAtual;

          const atributosNormTratTributarioCat = normalizeAtributosDinamicosNcm(
            unnormalizedData?.atributosTratamentoTributario
          );

          if (atributosNormTratTributarioCat && atributosNormTratTributarioCat.atributosSimples) {
            adicaoNormalizada.atributosSimplesTratamentoTributario = atributosNormTratTributarioCat.atributosSimples;
          }
        }
      }
    }

    if (adicaoNormalizada && adicaoNormalizada.atributosTratamentoTributario)
      delete adicaoNormalizada.atributosTratamentoTributario;

    return adicaoNormalizada;
  }, []);

  const handleDispatchSetModel = useCallback(
    (data) => {
      const normalizedData = normalize(data);

      dispatch(setModel(normalizedData));
    },
    [normalize]
  );

  const createByStep = async (data, step, next) => {
    const isFirstStep = step === 0;
    const isLastStep = step === 8;

    const executeDebounced = debounce(async () => {
      try {
        if (isFirstStep && !isCreated) {
          const response = await faturaAPI.register(data, [{ name: 'STEP', value: step + 1 }]);

          if (response?.status === 201) {
            next();
            dispatch(resetModel());
            dispatch(success());

            const created = response?.data;

            handleDispatchSetModel(created);
            dispatch(addToList({ data: created }));
            dispatch(fetchByIdFromFaturaAsync(created?.id));

            setIsCreated(true);
          }
        } else if (isLastStep) {
          const response = await faturaAPI.save(data, [{ name: 'STEP', value: step + 1 }]);

          if (response?.status === 200) {
            dispatch(resetModel());

            const handleResultWithDebounce = debounce(() => {
              history.push(
                t('com.muralis.qcx.url.importacaoDeclaracoesQuery', { processo: data?.atributosAdicionais?.processo })
              );

              dispatch(success());

              const saved = response?.data;

              dispatch(
                setSuccessFeedback({
                  status: response.status,
                  data: saved,
                  message: t('com.muralis.qcx.mensagem.DIDUIMPRegistrada', {
                    processo: data?.atributosAdicionais?.processo,
                  }),
                })
              );

              dispatch(updateOnList({ data: saved }));
            }, 500);

            handleResultWithDebounce();
          }
        } else {
          const response = await faturaAPI.save(data, [{ name: 'STEP', value: step + 1 }]);

          if (response?.status === 200) {
            if (isCreate) {
              next();
            } else {
              handleChangeToConsult();
            }

            const saved = response?.data;

            dispatch(success());
            dispatch(setModel(saved));
            dispatch(updateOnList({ data: saved }));
            dispatch(fetchByIdFromFaturaAsync(saved?.id));
          }
        }
      } catch (error) {
        dispatch(failure());
        let errorMessage = t('com.muralis.qcx.erro.erroSalvarDadosDIDUIMP');
        if (error?.response && error?.response?.data) {
          errorMessage = t('com.muralis.qcx.erro.erroSalvarDadosDIDUIMPEspecifico', {
            erro: error?.response?.data?.message,
          });
        }
        dispatch(
          setErrorFeedback({
            message: errorMessage,
          })
        );
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const parseToFormData = useCallback((data) => {
    const formData = new FormData();

    const json = JSON.stringify(data);
    const blob = new Blob([json], {
      type: MediaType.APPLICATION_JSON,
    });

    formData.append('data', blob);

    // É necessario passar o campo metadata vazio, pois o back tem esse campo como obrigatório
    const meta = { metadata: [] };
    const jsonMeta = JSON.stringify(meta);
    const blobMeta = new Blob([jsonMeta], {
      type: MediaType.APPLICATION_JSON,
    });

    formData.append('metadata', blobMeta);

    return formData;
  }, []);

  const update = async (data, step) => {
    const executeDebounced = debounce(async () => {
      try {
        const isCapaDiDuimp = step + 1 <= 7;
        const response = await faturaAPI.save(data, [
          { name: 'STEP', value: step + 1 },
          { name: 'isCapaDiDuimp', value: isCapaDiDuimp },
        ]);

        const formData = parseToFormData(data.followUp);
        const responseFollowUp = await followUpAPI.save(formData);

        if (response?.status === 200 && responseFollowUp.status === 200) {
          const handleResultWithDebounce = debounce(async () => {
            handleChangeToConsult();
            dispatch(success());

            const saved = response?.data;
            const savedFollowUp = responseFollowUp?.data;

            dispatch(
              setSuccessFeedback({
                message: t('com.muralis.qcx.mensagem.DIDUIMPSalva', { processo: data?.atributosAdicionais?.processo }),
              })
            );

            dispatch(setModel({ ...saved, followUp: savedFollowUp ?? saved?.followUp }));
            dispatch(updateOnList({ data: saved }));
            dispatch(fetchByIdFromFaturaAsync(saved?.id));
          }, 500);

          handleResultWithDebounce();
        }
      } catch ({ response }) {
        dispatch(failure());
        dispatch(
          setErrorFeedback({
            message: t('com.muralis.qcx.erro.erroSalvarDadosDIDUIMPProcesso', {
              processo: data?.atributosAdicionais?.processo,
              erro: response?.data?.message,
            }),
          })
        );
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const [isInformacoesComplementaresDialogOpen, setIsInformacoesComplementaresDialogOpen] = useState(false);
  const [handleSubmitArgs, setHandleSubmitArgs] = useState({});

  const submitWithCallback = async (data, step, next) => {
    setHandleSubmitArgs({ data, step, next });

    const alterouInformacoesComplementares =
      model?.atributosAdicionais?.informacoesComplementaresCalculada !== null &&
      data?.atributosAdicionais?.informacoesComplementaresCalculada !== null &&
      model?.atributosAdicionais?.informacoesComplementaresCalculada !==
        data?.atributosAdicionais?.informacoesComplementaresCalculada;

    if (alterouInformacoesComplementares && step === 0) {
      setIsInformacoesComplementaresDialogOpen(true);
    } else {
      handleSubmit(data, step, next);
    }
  };

  const handleSubmit = async (data, step, next) => {
    setIsInformacoesComplementaresDialogOpen(false);

    const normalizedData = normalize(data);
    if (isUpdate && !isBackgroundCreate) {
      await update(normalizedData, step);
      handleChangeToConsult();
      dispatch(resetNcmList());
      return;
    }

    if (isCreate || isBackgroundCreate) {
      await createByStep(normalizedData, step, next);
      dispatch(resetNcmList());
      return;
    }

    next();
  };

  const handleSubmitAuxiliar = async (data, step, next) => {
    const selectedAdicoesIds = data?.meta?.selected?.map((s) => s.id);

    const normalizedData = normalizeAuxiliar(data);

    const response = await declaracaoImportacaoAPI.updateAdicaoAuxiliar({
      adicaoBase: normalizedData,
      adicoesIds: selectedAdicoesIds,
    });

    if (response.status < 300) {
      handleChangeToConsult();

      dispatch(resetAdicoes());
      dispatch(fetchAdicoesByDiDuimpIdAsync(idDIDUIMP));
      dispatch(fetchPaginatedMercadoriasAsync({ faturaDiDuimpId: idDIDUIMP, page: 1, size: 10 }));

      dispatch(
        setSuccessFeedback({
          message: 'Dados repassados com sucesso!',
        })
      );
    } else {
      dispatch(
        setErrorFeedback({
          message: 'Houve um erro ao repassar os dados.',
        })
      );
    }
  };

  const handleAlternativeSave = async (event, step, next) => {
    if (event && !isEmpty(event)) {
      event.stopPropagation();
    }

    const normalizedData = normalize(declaracaoImportacao);

    if (isUpdate) {
      await update(normalizedData, step);
    } else {
      await createByStep(normalizedData, step, next);
    }
  };

  const handleCustomSaveBy = async (options) => {
    if (!isFunction(options?.composeWith)) {
      throw new Error('Required Function Error: "composeWith" is not a function.');
    }

    const unnormalizedData = options?.composeWith(declaracaoImportacao);

    const normalizedData = normalize(unnormalizedData);

    const response = await faturaAPI.save(normalizedData, [
      {
        name: 'STEP',
        value: options?.step,
      },
    ]);

    return response;
  };

  const refreshSelectedModel = useCallback(() => {
    if (!isEmpty(declaracaoImportacao) && declaracaoImportacao?.id) {
      fetchById(declaracaoImportacao?.id);
    }
  }, [declaracaoImportacao, isUpdate, fetchById, handleChangeToConsult]);

  const initialValuesForRegistration = useMemo(() => {
    let conta = declaracaoImportacao?.followUp?.importador?.contaBancariaImpostosFederais?.contaCorrente;
    let agencia = declaracaoImportacao?.followUp?.importador?.contaBancariaImpostosFederais?.agencia;

    if (declaracaoImportacao?.followUp?.importador?.contaBancariaImpostosFederais?.removerDigitoXml) {
      conta = conta?.slice(0, -1)?.replace('-', '');
    }

    if (declaracaoImportacao?.followUp?.importador?.contaBancariaImpostosFederais?.removerDigitoAgenciaXml) {
      agencia = agencia?.slice(0, -1)?.replace('-', '');
    }

    return {
      followUp: {
        ...declaracaoImportacao?.followUp,
        id: declaracaoImportacao?.followUp?.id,
      },
      importador: {
        id: declaracaoImportacao?.followUp?.importador?.id,
      },
      atributosAdicionais: {
        processo: declaracaoImportacao?.atributosAdicionais?.processo,
        tipoDeclaracao: {
          id: declaracaoImportacao?.followUp?.servico?.tipoDeclaracao?.id,
        },
        modalidadeDespacho: MODALIDADE_DESPACHO_NORMAL,
        documentosInstrucoesDespacho: declaracaoImportacao?.followUp?.documentos?.map((documento) => ({
          id: uuid(),
          documentoInstrucaoDespachoImportacao: documento?.documentoInstrucaoDespachoImportacao,
          identificacao: documento?.numero,
        })),
        transporte: {
          viaTransporte: {
            id: declaracaoImportacao?.followUp?.servico?.viaTransporte?.id,
          },
          utilizacao: UTILIZACAO_TRANSPORTE_TOTAL,
          identificacaoCrt: declaracaoImportacao?.followUp?.numeroConhecimentoHouse,
          presencaDeCarga: declaracaoImportacao?.followUp?.numeroPresencaCarga,
        },
        dadosBancarios: {
          banco: {
            id: declaracaoImportacao?.followUp?.importador?.contaBancariaImpostosFederais?.banco?.id,
          },
          agencia,
          conta,
          removerDigitoXml: declaracaoImportacao?.followUp?.importador?.contaBancariaImpostosFederais?.removerDigitoXml,
          removerDigitoAgenciaXml:
            declaracaoImportacao?.followUp?.importador?.contaBancariaImpostosFederais?.removerDigitoAgenciaXml,
        },
      },
      mle: { mesmaMoedaCondicaoVenda: true },
      seguro: {
        valorPercentual: declaracaoImportacao?.followUp?.importador?.pessoa?.valorPercentualSeguro,
        moeda: declaracaoImportacao?.followUp?.importador?.pessoa?.moeda,
        calculaSobreMleFrete:
          declaracaoImportacao?.followUp?.importador?.pessoa?.tipoCalculoSeguro === 'CALCULA_SOBRE_MLE_FRETE',
      },
    };
  }, [declaracaoImportacao]);

  const model = useMemo(
    () => (isCreated || !isCreate ? unnormalize(declaracaoImportacao) : initialValuesForRegistration),
    [isCreate, isCreated, unnormalize, declaracaoImportacao, initialValuesForRegistration]
  );

  const idDIDUIMP = useMemo(() => model?.atributosAdicionais?.id, [model]);

  const isLiberada = useMemo(() => isLiberadaStatus(model?.atributosAdicionais?.liberada), [model]);

  const actionName = useMemo(() => {
    if (isCreate || isBackgroundCreate || isNone) return t('com.muralis.qcx.acoes.novo');
    if (isConsult) return t('com.muralis.qcx.acoes.visualizar');
    return t('com.muralis.qcx.acoes.alterar');
  }, [isNone, isCreate, isConsult, isBackgroundCreate]);

  const breadcrumbs = useMemo(
    () => [
      {
        link: {
          to: '/',
          name: t('com.muralis.qcx.inicio'),
        },
      },
      {
        link: {
          to: t('com.muralis.qcx.url.moduloImportacao'),
          name: t('com.muralis.qcx.importacao.label'),
        },
      },
      {
        link: {
          to: t('com.muralis.qcx.url.importacaoDeclaracoesImportacao'),
          name: t('com.muralis.qcx.DIDUIMP.declaracoesImportacaoDIDUIMP'),
        },
      },
      {
        text: {
          name: isCreate ? t('com.muralis.qcx.registroManual') : t('com.muralis.qcx.registro'),
        },
      },
      {
        text: {
          name: actionName,
        },
      },
    ],
    [isCreate, actionName]
  );

  const pageTitle = useMemo(
    () =>
      isNone || isCreate || isBackgroundCreate
        ? t('com.muralis.qcx.DIDUIMP.novoRegistroManualDIDUIMNumero', {
            processo: model?.atributosAdicionais?.processo || '-',
          })
        : t('com.muralis.qcx.DIDUIMP.declaracaoImportacaoNumero', {
            processo: model?.atributosAdicionais?.processo || '-',
          }),
    [isNone, isCreate, isBackgroundCreate, model]
  );

  const handleBasicCancelUpdate = useCallback(
    (additional = {}) => {
      const currentId = isBackgroundCreate ? declaracaoImportacao?.id : params?.id;

      if (currentId) {
        fetchById(currentId);
      }

      handleChangeToConsult();

      if (!isEmpty(additional) && isFunction(additional?.callback)) {
        additional.callback();
      }
    },
    [params, declaracaoImportacao, isBackgroundCreate, fetchById, handleChangeToConsult]
  );

  const operationOfCancelUpdateOptions = useMemo(
    () => ({
      title: t('com.muralis.qcx.aviso').toUpperCase(),
      message: t('com.muralis.qcx.cancelarAlteracoesPerdidas'),
      endMessage: t('com.muralis.qcx.mensagem.certezaDesejaCancelar'),
    }),
    []
  );

  const [handleCancelUpdate, operationOfCancelUpdate] = useOperationConfirm(
    handleBasicCancelUpdate,
    operationOfCancelUpdateOptions,
    [handleBasicCancelUpdate, operationOfCancelUpdateOptions]
  );

  return (
    <QCXRegistrationFormPageTemplate
      pageTitle={pageTitle}
      breadcrumbs={breadcrumbs}
      isIdle={isIdle}
      isLoading={isLoading}
      isFailure={isFailure}
      isCreate={isCreate}
      isConsult={isConsult}
      isUpdate={isUpdate}
      isLocked={isLocked}
      isPreparingAction={isPreparingAction}
      isBackgroundCreate={isBackgroundCreate}
      isBackgroundDelete={isBackgroundDelete}
      isSubBackgroundCreate={isSubBackgroundCreate}
      isSubBackgroundConsult={isSubBackgroundConsult}
      isSubBackgroundUpdate={isSubBackgroundUpdate}
      isSubBackgroundDelete={isSubBackgroundDelete}
      handleAlternativeSave={handleAlternativeSave}
      handleChangeToPreparingAction={handleChangeToPreparingAction}
      handleChangeToCreate={handleChangeToCreate}
      handleChangeToBackgroundCreate={handleChangeToBackgroundCreate}
      handleChangeToBackgroundDelete={handleChangeToBackgroundDelete}
      handleChangeToConsult={handleChangeToConsult}
      handleChangeToUpdate={handleChangeToUpdate}
      handleCancelUpdate={handleCancelUpdate}
      handleResetBackgroundMode={handleResetBackgroundMode}
      showSubtitle={false}
      authInfo={authInfo}
      disableUpdate={isLiberada}
    >
      {(formProps) => (
        <QCXDeclaracaoImportacaoWizardFinalForm
          model={model}
          handleChangeModel={handleDispatchSetModel}
          handleSubmit={submitWithCallback}
          handleCustomSaveBy={handleCustomSaveBy}
          refreshSelectedModel={refreshSelectedModel}
          authInfo={authInfo}
          requiredRoles={['importacao-di-duimp']}
          openAskRepassarMoedasPopUp={openAskRepassarMoedasPopUp}
          openAskLimparAcrescimosDeducoes={openAskLimparAcrescimosDeducoes}
          handleSubmitAuxiliar={handleSubmitAuxiliar}
          {...formProps}
        >
          <QCXConfirmDialog
            open={operationOfCancelUpdate?.active}
            title={operationOfCancelUpdate?.title}
            content={operationOfCancelUpdate?.message}
            endContent={operationOfCancelUpdate?.endMessage}
            onConfirm={operationOfCancelUpdate.confirm}
            onClose={operationOfCancelUpdate?.reset}
            buttonGroupOptions={{
              confirm: {
                description: t('com.muralis.qcx.expressao.sim'),
              },
              cancel: {
                description: t('com.muralis.qcx.expressao.nao'),
              },
            }}
          />
          <QCXConfirmDialog
            open={askRepassarMoedasPopUpConfig?.isPopUpOpen}
            title={askRepassarMoedasPopUpConfig?.title}
            content={askRepassarMoedasPopUpConfig?.message}
            endContent={askRepassarMoedasPopUpConfig?.endMessage}
            onConfirm={askRepassarMoedasPopUpConfig.confirmPrimaryAction}
            onClose={askRepassarMoedasPopUpConfig?.reset}
            buttonGroupOptions={{
              confirm: {
                description: t('com.muralis.qcx.expressao.sim'),
              },
              cancel: {
                description: t('com.muralis.qcx.expressao.nao'),
              },
            }}
          />
          <QCXConfirmDialog
            open={askLimparAcrescimosDeducoesConfig?.isPopUpOpen}
            title={askLimparAcrescimosDeducoesConfig?.title}
            content={askLimparAcrescimosDeducoesConfig?.message}
            endContent={askLimparAcrescimosDeducoesConfig?.endMessage}
            onConfirm={askLimparAcrescimosDeducoesConfig.confirmPrimaryAction}
            onClose={askLimparAcrescimosDeducoesConfig?.reset}
            buttonGroupOptions={{
              confirm: {
                description: t('com.muralis.qcx.expressao.sim'),
              },
              cancel: {
                description: t('com.muralis.qcx.expressao.nao'),
              },
            }}
          />
          <QCXConfirmDialog
            open={isInformacoesComplementaresDialogOpen}
            title="Aviso de alteração das informações complementares"
            content="Atenção! Caso o processo seja recalculado as informações complementares referentes a cálculo de impostos serão atualizadas."
            onConfirm={() => handleSubmit(handleSubmitArgs.data, handleSubmitArgs.step, handleSubmitArgs.next)}
            onClose={() => setIsInformacoesComplementaresDialogOpen(false)}
            buttonGroupOptions={{
              confirm: {
                description: 'ATUALIZAR',
              },
              cancel: {
                description: 'CANCELAR',
              },
            }}
          />
        </QCXDeclaracaoImportacaoWizardFinalForm>
      )}
    </QCXRegistrationFormPageTemplate>
  );
}
